import { ApiError, SelectForm, SubmitButton } from "components";
import { RoleProps } from "modules/auth/role/store/actionType";
import {
  ActionProp,
  CreatePermissionInput,
  ModuleProp,
  PermissionProp,
  ResourceProp,
} from "../../store/actionType";
import { FormEvent, useEffect, useState } from "react";
import { fetchAllRoles } from "modules/auth/role/store/action";
import { connect } from "react-redux";
import { fetchPermission, setPermission } from "../../store/action";

interface FormProps {}
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type StateProps = ReturnType<typeof mapStateToProps>;
type Props = DispatchProps & StateProps & FormProps;
const PermissionForm: React.FC<Props> = ({ onFetchRoles, fetchAllRoles, onFetchPermission, fetchAllPermission, onSetPermission }) => {
  useEffect(() => {
    onFetchRoles();
    onFetchPermission(4, 0)
  }, [onFetchRoles, onFetchPermission]);
  const roleData: RoleProps[] = fetchAllRoles?.roles;
  const [selectedRole, setSelectedRole] = useState<string | undefined>("");
  const [existingAction, setExistingAction] = useState<string[]>([]);
  const [selectedResources, setSelectedResources] = useState<string[]>();
  const [selectedActions, setSelectedActions] =
    useState<string[]>(existingAction);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const handleRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedRole(e.target.value === "default" ? "" : e.target.value);
    setSelectedResources([]); // Reset selected resources
    setSelectedActions([]);
  };

  const handleResourceChange = (resource: ResourceProp) => {
    const resourceId = resource.id.toString();
    const actionIds = resource.actions.map((action: ActionProp) =>
      action.id.toString()
    );
    const updatedSelectedActions = selectedActions.filter(
      (item) => !actionIds.includes(item)
    );

    if (selectedResources?.includes(resourceId)) {
      setSelectedResources(
        selectedResources.filter((item) => item !== resourceId)
      );
      setSelectedActions(updatedSelectedActions);
    } else {
      setSelectedResources([...(selectedResources ?? []), resourceId]);
      setSelectedActions([...selectedActions, ...actionIds]);
    }
  };
  const handleActionChange = (action: ActionProp, resource: ResourceProp) => {
    const actionId = action.id.toString();
    const resourceId = resource.id.toString();
    // Get all selected actions under this resource
    const existingActionsUnderResource = resource?.actions
      ?.map((item) => item.id.toString())
      .filter((id) => selectedActions.includes(id));

    if (selectedActions.includes(actionId)) {
      setSelectedActions(selectedActions.filter((id) => id !== actionId));

      if (existingActionsUnderResource.length !== resource.actions.length) {
        setSelectedResources(
          selectedResources?.filter((id) => id !== resourceId)
        );
      }
    } else {
      setSelectedActions([...selectedActions, actionId]);
      if (existingActionsUnderResource.length + 1 === resource.actions.length) {
        setSelectedResources([...(selectedResources ?? []), resourceId]);
      }
    }
  };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setApiError(null);
    try {
      const input: CreatePermissionInput = {
        role_id: selectedRole!,
        action_ids: selectedActions, 
      };
      await onSetPermission(input); 

    } catch (error: any) {
      setApiError(error?.message || "Something went wrong.");
    } finally {
      setLoading(false); 
    }
};

  useEffect(() => {
  if (!selectedRole || !fetchAllPermission?.permission?.length) return;

  const newExistingActions: string[] = [];
  const newExistingResources: string[] = [];

  fetchAllPermission.permission.forEach((module: ModuleProp) => {
    module.resources?.forEach((resource: ResourceProp) => {
      const newResourceActions: string[] = [];
      resource.actions?.forEach((action: ActionProp) => {
        if (
          action.role &&
          action.role.some((role: RoleProps) => role.id.toString() === selectedRole)
        )
         {
          newExistingActions.push(action.id.toString());
          newResourceActions.push(action.id.toString());
        }
      });

      if (
        newResourceActions.length > 0 &&
        newResourceActions.length === resource.actions.length
      ) {
        newExistingResources.push(resource.id.toString());
      }
    });
  });

  setExistingAction(newExistingActions);
  setSelectedActions(newExistingActions);
  setSelectedResources(newExistingResources);
}, [selectedRole, fetchAllPermission]);


  return (
    <>
      <form className="mt-5 ">
        <div className="mt-4 max-w-sm p-5">
          <h2 className="text-xl font-medium">
            {" "}
            Select a role to set the permission
          </h2>
          <SelectForm
            label="Role"
            styleLabel="font-bold text-sm"
            id="role"
            name="role"
            value={selectedRole}
            onChange={handleRoleChange}
            option={roleData?.map((role: RoleProps) => (
              <option key={role.id.toString()} value={role.id.toString()}>
                {role.name}
              </option>
            ))}
          />
        </div>
        {selectedRole && (
          <div className="p-5 flex flex-col">
            {(fetchAllPermission?.permission ?? []).map((permission: PermissionProp) => (
              <div key={permission?.id?.toString()}>
                <h3 className="">{permission.name}</h3>
                <hr className="h-px my-2 bg-gray-300 border-0 dark:bg-gray-700" />
                <div className="grid grid-cols-3 md:grid-cols-4 gap-10 m-4">
                  {permission.resources?.map((resource) => (
                    <div
                      className="h-auto max-w-full rounded-lg bg-gray-200 p-4"
                      key={resource.id.toString()}
                    >
                      <div className="flex items-center justify-between">
                        <div className="text-black text-sm">
                          {resource?.name}
                        </div>
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={selectedResources?.includes(
                              resource.id.toString()
                            )}
                            id={resource.id.toString()}
                            name={resource.id.toString()}
                            // readOnly={isPermitted}
                            onChange={() => handleResourceChange(resource)}
                            className="w-4 h-4 p-4 focus:ring-1 focus:ring-offset-slate-200 focus:ring-offset-4 focus:ring-[#007bff]"
                          />
                          <label className="text-black ml-2 text-sm">
                            Select all
                          </label>
                        </div>
                      </div>
                      <hr className="h-px my-2 bg-gray-300 border-0 dark:bg-gray-700" />
                      <div className="flex flex-wrap mt-3">
                        {resource?.actions?.map((action) => (
                          <div
                            className="flex items-center p-2"
                            key={action.id.toString()}
                          >
                            <input
                              type="checkbox"
                              id={action.id.toString()}
                              name={action.id.toString()}
                              //   readOnly={isPermitted}
                              checked={selectedActions.includes(
                                action.id.toString()
                              )}
                              onChange={() =>
                                handleActionChange(action, resource)
                              }
                              className="w-4 h-4 mr-3 focus:ring-1 focus:ring-offset-slate-200 focus:ring-offset-4 focus:ring-[#007bff]"
                            />
                            <label className="text-black text-sm ml-1">
                              {action?.name}{""}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {fetchAllPermission && (
              <div className="w-full flex justify-end mt-5">
                <SubmitButton
                  type="button"
                  onClick={handleSave}
                  loading={loading}
                  loadingText="Setting..."
                  className="bg-primary-300 text-white font-semibold px-9 py-2 rounded-lg shadow-md hover:bg-primary-500 hover:shadow-lg hover:scale-105 transition-all duration-300 ease-in-out transform"
                >
                  Save
                </SubmitButton>
              </div>
            )}
            {loading && <div>Loading...</div>}
            {apiError && <ApiError>{apiError}</ApiError>}
          </div>
        )}
      </form>
    </>
  );
};
const mapStateToProps = (state: any) => {
  return {
    fetchAllRoles: state.roles?.fetchRolesState,
    fetchAllPermission: state.permission?.fetchPermissionState,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  onFetchRoles: () => dispatch(fetchAllRoles()),
  onFetchPermission: (limit: number, offset: number) => dispatch(fetchPermission(limit, offset)),
  onSetPermission: (input: CreatePermissionInput) =>
    dispatch(setPermission(input)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PermissionForm);
